// import hooks
import { usePlanUnitsOptions } from './usePlanUnitsOptions';

// import types
import type {
  OrganizationalUnitId,
  OrganizationalUnitOption
} from 'utils/organizationalUnits';

/**
 * The remembered plan unit ids as the plan units themselves.
 *
 * What is remembered across visits is ids, not plan units: a plan unit is a record with a validity window
 * and cost centres of its own, so a stored copy would go quietly out of date — and the employee and function
 * lists are asked for its cost centres, which is the last thing that should be answered from a stale
 * snapshot. Resolving here against the plan units the app has loaded keeps them current, and an id that no
 * longer resolves drops out: a plan unit that has gone stops filtering rather than filtering on nothing.
 *
 * The same options list the field itself shows, so a resolved pick is always one the planner can see and
 * untick again.
 *
 * Also reports whether it is still waiting on that list: until the options arrive a remembered pick
 * resolves to nothing, and a request sent then is scoped to no plan unit at all — it answers "no data" and
 * is followed by a second, real request once the list lands. The options list is the only signal available
 * (it is Redux-backed and carries no loading flag), so "still empty" is what waiting means. It lifts as
 * soon as the list arrives, whether or not the remembered id is still in it, so a pick that has gone
 * cannot hold the page.
 */
export const usePickedOrganizationalUnits = (
  organizationalUnitIds: OrganizationalUnitId[]
): {
  organizationalUnits: OrganizationalUnitOption[];
  isAwaitingOptions: boolean;
} => {
  const options = usePlanUnitsOptions({ showGroups: true });

  return {
    organizationalUnits: options.filter(({ uniqId }) =>
      organizationalUnitIds.includes(uniqId)
    ),
    isAwaitingOptions: organizationalUnitIds.length > 0 && options.length === 0
  };
};
